import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { saveAs } from 'file-saver'
import { useAccessToken, useCurrentUser } from '@/features/auth/state/auth'
import api from '@/lib/api'

export type TipoFiscal = 'CREDITO' | 'DEBITO'

export type TaxLancamento = {
  id: string
  id_apuracao_competencia: string
  id_type_fiscal: string
  typeFiscalType: TipoFiscal | null
  anoCompetencia: string | null
  mesCompetencia: string | null
  name: string | null
  valueOriginal: number | null
  valuePago: number | null
  valueUtilizado: number | null
  valueSaldo: number | null
  valueSelic: number | null
  honorarios: number | null
  totalCompensado: number | null
  perDcomp: string | null
  situacaoPerDcomp: boolean
  situacaoPago: boolean
  idTenant: string | null
  createdDate: Date
}

export type CreateTaxLancamentoInput = {
  id_apuracao_competencia: string
  typeFiscalType: TipoFiscal
  anoCompetencia?: string | null
  mesCompetencia?: string | null
  name?: string | null
  valueOriginal?: number | null
  valuePago?: number | null
  valueUtilizado?: number | null
  valueSaldo?: number | null
  valueSelic?: number | null
  honorarios?: number | null
  totalCompensado?: number | null
  perDcomp?: string | null
  situacaoPerDcomp?: boolean
  situacaoPago?: boolean
}

export type UpdateTaxLancamentoInput = Partial<
  Omit<CreateTaxLancamentoInput, 'id_apuracao_competencia' | 'typeFiscalType'>
> & {
  id: string
}

const taxLancamentosQueryKey = (idApuracaoCompetencia?: string) => ['tax-lancamentos', idApuracaoCompetencia]

export function useFetchTaxLancamentos(idApuracaoCompetencia?: string) {
  const user = useCurrentUser()
  const token = useAccessToken()

  return useQuery({
    queryKey: taxLancamentosQueryKey(idApuracaoCompetencia),
    queryFn: async () => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      if (!idApuracaoCompetencia) throw new Error('ID da apuração competência ausente')

      const response = await api.get<TaxLancamento[]>(`/tax-lancamentos/apuracao-competencia/${idApuracaoCompetencia}`)
      return response.data
    },
    enabled: Boolean(user && token && idApuracaoCompetencia)
  })
}

export function useCreateTaxLancamento() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (body: CreateTaxLancamentoInput) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.post<TaxLancamento>('/tax-lancamentos', body)
      return response.data
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: taxLancamentosQueryKey(data.id_apuracao_competencia) })
    }
  })
}

export function useUpdateTaxLancamento() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateTaxLancamentoInput) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.put<TaxLancamento>(`/tax-lancamentos/${id}`, body)
      return response.data
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: taxLancamentosQueryKey(data.id_apuracao_competencia) })
    }
  })
}

export function useDeleteTaxLancamento() {
  const queryClient = useQueryClient()
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (lancamento: Pick<TaxLancamento, 'id' | 'id_apuracao_competencia'>) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.delete<TaxLancamento>(`/tax-lancamentos/${lancamento.id}`)
      return response.data
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: taxLancamentosQueryKey(data.id_apuracao_competencia) })
    }
  })
}

export function useExportPrevidenciarioTaxPlanilhas() {
  const user = useCurrentUser()
  const token = useAccessToken()

  return useMutation({
    mutationFn: async (idApuracaoCompetencia: string) => {
      if (!user || !token) throw new Error('Usuário ou token ausente')
      const response = await api.get<Blob>(`/tax/planilhas/previdenciario/${idApuracaoCompetencia}`, {
        responseType: 'blob'
      })
      saveAs(response.data, `previdenciario-tax-${idApuracaoCompetencia}.xlsx`)
    }
  })
}
